import { ApolloError } from '@apollo/client';
import { useDisclosure } from '@chakra-ui/react';
import getAbortEnhancer from '@rpldy/abort';
import retryEnhancer from '@rpldy/retry-hooks';
import TusUploady from '@rpldy/tus-uploady';
import { composeEnhancers } from '@rpldy/uploady';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import { ConfirmLogoutOnUploadDialog } from '../../../components/shared/dialogs/ConfirmLogoutOnUploadDialog/ConfirmLogoutOnUploadDialog';
import { VideoFragment } from '../../../generated/graphql';
import { useBeforeUnload } from '../../../hooks/useBeforeUnload';
import { createContext } from '../../../hooks/useContext';
import { routes } from '../../../routes/routesConfig';
import { noop } from '../../../utils';
import { useAuth } from '../../AuthProvider';
import {
  TusUploadEntry,
  useTusVideoUploadEnhancer,
} from './videoUploadEnhancer';
import { useTusVideoUploadHistoryEnhancer } from './videoUploadHistoryEnhancer';
import { tusVideoUploadLogEnhancer } from './videoUploadLogEnhancer';

interface UploadyServiceContext {
  uploads: TusUploadEntry[];
  videos: VideoFragment[];
  initialLoading: boolean;
  videosLoading: boolean;
  videosError: ApolloError | undefined;
  unusedTotalCount: number;
  unusedMaxCount: number;
  isUploadAllowed: boolean;
  isUploading: boolean;
  loadMore: () => void;
  onDelete: (albumId: number) => void;
}

export const [, useUploadyService, uploadyServiceContext] =
  createContext<UploadyServiceContext>({
    name: 'UploadyServiceContext',
    errorMessage:
      'useUploadyService: `uploadyServiceContext` is undefined. Seems you forgot to wrap component within the Provider',
    strict: true,
  });

export const UploadyServiceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const authCtx = useAuth();
  const history = useHistory();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const unusedMaxCount = 200;

  // get information about the current uploads
  const [
    tusVideoUploadEnhancer,
    tusUploads,
    filterTusUploads,
    hasUnfinishedUploads,
  ] = useTusVideoUploadEnhancer();

  // get information about uploaded videos
  const [
    tusVideoUploadHistoryEnhancer,
    videos,
    initialLoading,
    videosLoading,
    videosError,
    totalItems,
    loadMore,
    onDelete,
  ] = useTusVideoUploadHistoryEnhancer(filterTusUploads);

  //check for unfinished uploads before leaving page
  useBeforeUnload(
    React.useMemo(() => {
      return hasUnfinishedUploads;
    }, [hasUnfinishedUploads])
  );

  // check for unfinished uploads before logout
  const historyUnblock = React.useRef<() => void>(noop);

  const [modalNavigateBackCallback, setModalNavigateBackCallback] =
    React.useState<() => void>(noop);

  React.useEffect(() => {
    historyUnblock.current = history.block(({ pathname }): any => {
      if (hasUnfinishedUploads && pathname === routes.logout.path) {
        setModalNavigateBackCallback(() => {
          return () => {
            historyUnblock.current();
            history.push(pathname);
            onClose();
          };
        });
        onOpen();
        return false;
      }

      return true;
    });

    return () => {
      historyUnblock.current();
    };
  }, [hasUnfinishedUploads, history, onClose, onOpen]);

  /**
   * This enhancer allows:
   * 1.logging of upload events
   * 2.handling of current uploads
   * 3.handling of past uploads
   * 4.enables retries for uploads
   */
  const enhancer = React.useMemo(() => {
    return composeEnhancers(
      tusVideoUploadLogEnhancer,
      tusVideoUploadEnhancer,
      tusVideoUploadHistoryEnhancer,
      retryEnhancer,
      getAbortEnhancer()
    );
  }, [tusVideoUploadEnhancer, tusVideoUploadHistoryEnhancer]);

  const context: UploadyServiceContext = React.useMemo(() => {
    return {
      uploads: tusUploads,
      videos,
      initialLoading,
      videosLoading,
      videosError,
      unusedTotalCount: totalItems,
      unusedMaxCount,
      isUploadAllowed: totalItems < unusedMaxCount,
      isUploading: tusUploads.length !== 0,
      loadMore,
      onDelete,
    };
  }, [
    tusUploads,
    videos,
    initialLoading,
    videosLoading,
    videosError,
    totalItems,
    loadMore,
    onDelete,
  ]);

  return (
    <TusUploady
      key={authCtx.authUser?.userId}
      autoUpload={false}
      multiple={false}
      accept="video/*"
      enhancer={enhancer}
      forgetOnSuccess={true}
      chunkSize={8_000_000}
      // Mitigate Request Header Upload-Meta not set by disabling parallel option
      // todo: investigate cause of missing header
      // parallel={4}
      storagePrefix={`Uploady_${authCtx?.authUser?.userId}_`}
    >
      <uploadyServiceContext.Provider value={context}>
        <ConfirmLogoutOnUploadDialog
          confirmLogout={modalNavigateBackCallback}
          isOpen={isOpen}
          onClose={onClose}
        />
        {children}
      </uploadyServiceContext.Provider>
    </TusUploady>
  );
};
export const UserWrappedUploadyProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const authCtx = useAuth();

  return (
    <UploadyServiceProvider
      children={children}
      key={authCtx.authUser?.userId}
    />
  );
};
